import React, { useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { connect } from 'react-redux';
import { Form } from 'reactstrap';

import Loader from '../../../common/Loader';
import Alert from '../../../common/informative/Alert';
import DownloadButton from '../../../common/button/DownloadButton';
import Table from '../../../common/Table';

import { THERE_ARE_NOT_DATA_TO_SHOW } from '../../../../config/messages';
import { INT_DATE_TIME_FORMAT } from '../../../../config/locale';
import { tzNormalizeDate } from '../../../../utils/date';
import { filterMethodCaseInsensitive } from '../../../../utils/filter-methods';

import { RESOURCE_EXECUTION_MODE } from '../../../../config/constants';
import { clearPreventiveTaskExecutions } from '../../../../actions/task-schedule/PreventiveTaskExecution';
import { PREVENTIVE_TASK_EXECUTION_COLUMNS } from '../../../../config/columns';

const getEnumLabel = (enumObject, value) => {
  if (!value || !enumObject || !enumObject[value]) return value || '-';

  return enumObject[value].label || value;
};

const getPreventiveTaskName = (row) => {
  if (row.preventiveTask && row.preventiveTask.name) {
    return row.preventiveTask.name;
  }

  if (row.preventiveTaskName) {
    return row.preventiveTaskName;
  }

  return '-';
};

const getExecutorName = (row) => {
  if (row.user && row.user.fullName) {
    return row.user.fullName;
  }

  if (row.user && row.user.name) {
    return row.user.name;
  }

  return row.executorUserId || '-';
};

const convertDataToCsv = (response) =>
  response.map((row) => ({
    Tarea: getPreventiveTaskName(row),
    'Fecha Programada': row.scheduleDate
      ? tzNormalizeDate({
          date: row.scheduleDate,
          format: INT_DATE_TIME_FORMAT,
        })
      : '-',
    'Fecha Inicio': row.startedDate
      ? tzNormalizeDate({
          date: row.startedDate,
          format: INT_DATE_TIME_FORMAT,
        })
      : '-',
    'Fecha Fin': row.completedDate
      ? tzNormalizeDate({
          date: row.completedDate,
          format: INT_DATE_TIME_FORMAT,
        })
      : '-',
    Modo: getEnumLabel(RESOURCE_EXECUTION_MODE, row.resourceExecutionMode),
    Ejecutor: getExecutorName(row),
    Comentario: row.comment || '-',
  }));

export const PreventiveTaskExecutionHistoryResult = ({
  dispatchClearPreventiveTaskExecutions,
  loading,
  content,
  handleSubmit,
}) => {
  useLayoutEffect(() => () => dispatchClearPreventiveTaskExecutions(), []);

  const data = content.get('content') || [];

  let table = <Alert message={THERE_ARE_NOT_DATA_TO_SHOW} />;

  if (loading) table = <Loader />;

  if (!loading && data.length > 0) {
    const downloadButton = (
      <div className="mb-1 text-right">
        <DownloadButton
          data={convertDataToCsv(data)}
          csvName="Historial de Ejecuciones Preventivas.csv"
        />
      </div>
    );

    table = (
      <>
        {downloadButton}

        <Form onSubmit={handleSubmit}>
          <Table
            columns={PREVENTIVE_TASK_EXECUTION_COLUMNS}
            data={data}
            manual={false}
            loading={loading}
            defaultPageSize={100}
            required
            defaultFilterMethod={filterMethodCaseInsensitive}
            filterable
            getTbodyProps={() => ({ className: 'body-scrollable' })}
          />
        </Form>
      </>
    );
  }

  return table;
};

PreventiveTaskExecutionHistoryResult.propTypes = {
  content: PropTypes.instanceOf(Immutable.Map).isRequired,
  loading: PropTypes.bool,
  dispatchClearPreventiveTaskExecutions: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func,
};

PreventiveTaskExecutionHistoryResult.defaultProps = {
  loading: false,
  handleSubmit: () => {},
};

const mapStateToProps = ({ TaskScheduleUnit }) => ({
  loading: TaskScheduleUnit.PreventiveTaskExecution.getIn(['all', 'loading']),
  content: TaskScheduleUnit.PreventiveTaskExecution.getIn(['all', 'content']),
});

const mapDispatchToProps = {
  dispatchClearPreventiveTaskExecutions: clearPreventiveTaskExecutions,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PreventiveTaskExecutionHistoryResult);
